import { AptiveIcon } from '@aptive-env/storybook'
import Popover from 'components/shared/Popover'
import { format, parse, addDays } from 'date-fns'
import { ICalendarEvent, ICalendarEventType } from 'interface/calendar'
import { FC, useCallback, useState } from 'react'
import { fetchCalendarEvents } from 'services/calendar'
import { getCalendarEventTypeColors } from 'utils/color'
import { convert24HourTo12Hour } from 'utils/convertHour'

interface IDay {
  day: number
  month: number
  year: number
  isCurrentMonth: boolean
  isCurrentDay: boolean
}

const WEEK_DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

// Function to get the number of days in a month
const getDaysInMonth = (month: number, year: number): number => {
  return new Date(year, month + 1, 0).getDate()
}

// Function to get the day of the week for the first day of the month
const getFirstDayOfMonth = (month: number, year: number): number => {
  return new Date(year, month, 1).getDay()
}

interface MonthProps {
  month: number // Month number (0 for January, 1 for February, ..., 11 for December)
  year: number
  officeId: number,
  eventTypes: ICalendarEventType[]
}

const Month: FC<MonthProps> = ({ month, officeId, year, eventTypes }) => {
  const currentDate = new Date()
  const currentYear = currentDate.getFullYear()
  const currentMonth = currentDate.getMonth()
  const currentDay = currentDate.getDate()

  const daysInMonth = getDaysInMonth(month, year)
  const firstDayOfMonth = getFirstDayOfMonth(month, year)

  // Generate an array of days in the current month including days from the previous and next months
  const days: IDay[] = []
  const daysInPreviousMonth = getDaysInMonth(month - 1 < 0 ? 11 : month - 1, month - 1 < 0 ? year - 1 : year)
  // const daysInNextMonth = getDaysInMonth(month + 1 > 11 ? 0 : month + 1, month + 1 > 11 ? year + 1 : year)
  const previousMonthStart = daysInPreviousMonth - firstDayOfMonth + 1

  // Add days from the previous month
  for (let i = previousMonthStart; i <= daysInPreviousMonth; i++) {
    days.push({ day: i, month: month - 1 < 0 ? 11 : month - 1, year: month - 1 < 0 ? year - 1 : year, isCurrentMonth: false, isCurrentDay: false })
  }

  // Add days from the current month
  for (let i = 1; i <= daysInMonth; i++) {
    days.push({ day: i, month, year, isCurrentMonth: true, isCurrentDay: currentMonth === month && currentDay === i && currentYear === year })
  }

  // Add days from the next month
  let nextMonthDay = 1
  while (days.length % 14 !== 0) {
    days.push({ day: nextMonthDay, month: month + 1 > 11 ? 0 : month + 1, year: month + 1 > 11 ? year + 1 : year, isCurrentMonth: false, isCurrentDay: false })
    nextMonthDay++
  }

  const [popoverData, setPopoverData] = useState<{ day: IDay, isLoading: boolean } | null>(null)
  const [dayEvents, setDayEvents] = useState<ICalendarEvent[]>([])

  const handleViewDayEvents = async (day: IDay) => {
    setPopoverData({
      day,
      isLoading: true
    })
    setDayEvents([])
    const startDate = parse(`${day.year}-${(day.month + 1).toString().padStart(2, '0')}-${day.day.toString().padStart(2, '0')}`, 'yyyy-MM-dd', new Date())
    const startDateStr = format(startDate, 'yyyy-MM-dd')
    const endDateStr = format(addDays(startDate, 1), 'yyyy-MM-dd')
    const response = await fetchCalendarEvents({
      office_id: officeId,
      start_date: startDateStr,
      end_date: endDateStr,
      per_page: 100
    })
    setPopoverData({
      day,
      isLoading: false,
    })
    setDayEvents(response.result.events[startDateStr] || [])
  }

  const filteredDayEvents = useCallback(() => {
    return dayEvents
      .filter((event) => eventTypes.map((type: ICalendarEventType) => type.id).includes(event.event_type))
      .sort((eventA, eventB) => eventA.start_at.localeCompare(eventB.start_at)) ?? []
  }, [dayEvents, eventTypes])

  const getEventModalPosition = (month: number) => {
    if ([0, 1, 4, 5].includes(month)) return 'right-top'
    if ([2, 3, 6, 7].includes(month)) return 'left-top'
    if ([8, 9].includes(month)) return 'right-bottom'
    if ([10, 11].includes(month)) return 'left-bottom'
  } 

  const isPastEvent = (eventDate: Date, eventStartAt: string) => {
    const currentDate = new Date()
    const currentTime = `${currentDate.getHours().toString().padStart(2, '0')}:${currentDate.getMinutes().toString().padStart(2, '0')}:${currentDate.getSeconds().toString().padStart(2, '0')}`
    if (eventDate.setHours(0, 0, 0, 0) < currentDate.setHours(0, 0, 0, 0)) {
      return true
    }
    console.log(currentTime, eventStartAt < currentTime)
    return eventStartAt < currentTime
  }

  return (
    <div className="border-b border-r border-ui-gray-100 p-4">
      <div className="text-center text-base text-ui-gray-900 my-3">{MONTHS[month]}</div>
      <div className="grid grid-cols-7 my-3">
        {WEEK_DAYS.map((weekDay, weekDayIndex) => (
          <div
            key={weekDayIndex}
            className="flex items-center justify-center w-[38px] h-[16px] text-sm text-ui-gray-500 mb-2"
          >
            {weekDay}
          </div>
        ))}
        {days.map((day, dayIndex) => (
          <Popover
            key={dayIndex}
            open={popoverData?.day?.day === day.day && popoverData?.day?.month === day.month && popoverData?.day?.year === day.year}
            eventModalPosition={getEventModalPosition(month)}
            content={
              <div className="p-3">
                {popoverData?.isLoading ? (
                  <div className="text-sm text-aro-gray">Loading</div>
                ) : (
                  <div>
                    <div className="w-8 m-auto">
                      <div className="text-xs font-medium text-center uppercase">{format(parse(`${day.year}-${(day.month + 1).toString().padStart(2, '0')}-${day.day.toString().padStart(2, '0')}`, 'yyyy-MM-dd', new Date()), 'EEE')}</div>
                      <div className="bg-[#007AFF] rounded-lg text-white text-center">{day.day}</div>
                    </div>
                    <div className="absolute right-1 top-3">
                      <AptiveIcon
                        icon="x"
                        className="w-[16px] h-[16px] stroke-[#3D3D3D] fill-none cursor-pointer"
                        onClick={() => setPopoverData(null)}
                      />
                    </div>
                    {(filteredDayEvents().length > 0) ? (
                      <ul className="mt-3">
                        {filteredDayEvents().map((event) => {
                          if (event.is_canceled === 'false')
                            return (
                              <li className="flex mt-2" key={event.id}>
                                <div
                                  className="w-[12px] h-[12px] rounded-[50%] mt-[2px]"
                                  style={{ background: getCalendarEventTypeColors(event.event_type).dark }}
                                />
                                <div className="ml-2">
                                  <div className="flex">
                                    <div className={`text-[11px] text-[#3D3D3D] whitespace-nowrap
                                      ${isPastEvent(parse(`${day.year}-${(day.month + 1).toString().padStart(2, '0')}-${day.day.toString().padStart(2, '0')}`, 'yyyy-MM-dd', new Date()), event.start_at) ? 'line-through' : ''}
                                    `}>
                                      {convert24HourTo12Hour(event.start_at)} <span className="font-bold">{event.title}</span>
                                    </div>
                                  </div>
                                </div>
                              </li>
                            )
                          else
                            return <></>
                        })}
                      </ul>
                    ) : (
                      <div className="whitespace-nowrap mt-3 text-sm text-aro-gray">No events</div>
                    )}
                  </div>
                )}
              </div>
            }
            onClickOutside={() => {
              setPopoverData(null)
            }}
          >
            <div
              className={`flex items-center justify-center text-sm border-r border-b border-ui-gray-100 h-[38px] hover:bg-ui-gray-200 cursor-pointer ${day.isCurrentMonth ? 'text-ui-gray-900' : 'text-ui-gray-500'}`}
              onClick={() => {
                handleViewDayEvents(day)
              }}
            >
              {day.isCurrentDay ? (
                <div className="bg-[#007AFF] text-white w-full h-[32px] rounded-lg flex items-center justify-center">{day.day}</div>
              ) : (
                <div>{day.day}</div>
              )}
            </div>
          </Popover>
        ))}
      </div>
    </div>
  )
}

interface CalendarYearViewProps {
  officeId: number
  year: number,
  eventTypes: ICalendarEventType[]
}

const CalendarYearView: FC<CalendarYearViewProps> = ({
  officeId,
  year,
  eventTypes
}) => {
  return (
    <div className="border-[#CBCBCB80] flex-auto overflow-auto">
      <div className="grid grid-cols-4">
        {Array.from({ length: 12 }, (_, i) => i + 1).map((_, monthIndex) => (
          <Month
            key={monthIndex}
            officeId={officeId}
            month={monthIndex}
            year={year}
            eventTypes={eventTypes}
          />
        ))}
      </div>
    </div>
  )
}

export default CalendarYearView
